import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { SheetHeader, SheetTitle } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/utils';

const CartSheet = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartTotal, updateCartItem, removeFromCart, setIsOpen, isOpen } = useCart();
  const scrollRef = useRef(null);

  // Helper to get translated removed ingredients
  const getTranslatedRemoved = (item) => {
    if (!item.removed_ingredients?.length) return [];
    const lang = i18n.language;
    const engList = item.ingredients || [];
    const transMap = { it: item.ingredients_it, de: item.ingredients_de, ar: item.ingredients_ar };
    const translatedList = transMap[lang];
    if (!translatedList || translatedList.length !== engList.length) return item.removed_ingredients;
    return item.removed_ingredients.map(eng => {
      const idx = engList.indexOf(eng);
      return idx >= 0 ? translatedList[idx] : eng;
    });
  };

  // Helper to get localized item name
  // Item names always shown in English (as created)
  const getLocalizedName = (item) => item.name;

  // Helper to get localized addon name
  const getLocalizedAddonName = (addon) => {
    const lang = i18n.language;
    if (lang === 'it' && addon.name_it) return addon.name_it;
    if (lang === 'de' && addon.name_de) return addon.name_de;
    if (lang === 'fr' && addon.name_fr) return addon.name_fr;
    if (lang === 'ar' && addon.name_ar) return addon.name_ar;
    return addon.name;
  };

  // Reset scroll to top when cart sheet opens
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) viewport.scrollTop = 0;
    }
  }, [isOpen]);

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <ShoppingBag className="h-16 w-16 text-zinc-600 mb-4" />
        <h3 className="text-xl font-semibold mb-2">{t('cart.empty')}</h3>
        <p className="text-zinc-400 text-center mb-6">
          {t('cart.emptyDesc')}
        </p>
        <Link to="/menu" onClick={() => setIsOpen(false)}>
          <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide" data-testid="browse-menu-button">
            {t('hero.viewMenu')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SheetHeader>
        <SheetTitle className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed']">
          {t('cart.title')}
        </SheetTitle>
      </SheetHeader>

      <ScrollArea ref={scrollRef} className="flex-1 -mx-6 px-6 py-4">
        <div className="space-y-4">
          {cart.items.map((item) => (
            <div
              key={item.menu_item_id}
              className="flex gap-4 p-3 rounded-lg bg-[#141417]/60 border border-[#27272A]"
              data-testid={`cart-item-${item.menu_item_id}`}
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-white">
                  {getLocalizedName(item)}
                  {item.selected_size && (
                    <span className="text-xs text-zinc-400 font-normal ml-1">
                      ({item.selected_size === 'big' ? t('itemDetail.sizeBig') : t('itemDetail.sizeNormal')})
                    </span>
                  )}
                </h4>
                <p className="text-[#FFC107] font-bold">
                  {formatPrice((item.selected_size === 'big' && item.price_big) ? item.price_big : item.price)}
                  {item.has_combo && <span className="text-xs text-zinc-400 font-normal"> + CHF 6.00 combo</span>}
                  {item.selected_addons?.length > 0 && (
                    <span className="text-xs text-zinc-400 font-normal"> + CHF {item.selected_addons.reduce((s,a) => s + a.price, 0).toFixed(2)} extras</span>
                  )}
                </p>
                {item.has_combo && (
                  <p className="text-xs text-green-400 mt-0.5" data-testid={`combo-label-${item.menu_item_id}`}>
                    {t('itemDetail.comboTitle')}
                  </p>
                )}
                {item.selected_addons?.length > 0 && (
                  <p className="text-xs text-[#FFC107]/70 mt-0.5" data-testid={`addons-label-${item.menu_item_id}`}>
                    {t('itemDetail.extras')}: {item.selected_addons.map(a => getLocalizedAddonName(a)).join(', ')}
                  </p>
                )}
                {item.special_instructions && (
                  <p className="text-xs text-zinc-400 mt-1">{item.special_instructions}</p>
                )}
                {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                  <p className="text-xs text-red-400 mt-1">
                    {t('itemDetail.withoutItems', { items: getTranslatedRemoved(item).join(', ') })}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-zinc-700"
                      onClick={() => updateCartItem(item.menu_item_id, item.quantity - 1)}
                      data-testid={`decrease-${item.menu_item_id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-zinc-700"
                      onClick={() => updateCartItem(item.menu_item_id, item.quantity + 1)}
                      data-testid={`increase-${item.menu_item_id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    onClick={() => removeFromCart(item.menu_item_id)}
                    data-testid={`remove-${item.menu_item_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t border-[#27272A] pt-4 mt-4">
        <div className="flex justify-between items-center mb-4">
          <span className="text-zinc-400">{t('cart.subtotal')}</span>
          <span className="text-xl font-bold text-[#FFC107]">{formatPrice(cartTotal)}</span>
        </div>
        <Button
          onClick={handleCheckout}
          className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide btn-glow relative z-50"
          data-testid="checkout-button"
        >
          {t('cart.checkout')}
        </Button>
      </div>
    </div>
  );
};

export default CartSheet;
